import { Injectable } from '@angular/core';
import { ProductWithChildrenV2 } from '../../models/productv2/productv2-children.model';
import { EnumProductType } from '../../models/enum/enum-product-type';
import { ProductV2 } from '../../models/productv2/productv2.model';
import { KitProductV2 } from '../../models/productv2/kit-productv2.model';
import { PackProductV2 } from '../../models/productv2/pack-productv2.model';
import { AtomicProductV2 } from '../../models/productv2/atomic-productv2.model';
import { PriceList } from '../../models/productv2/pricelist.model';
import { ProductPricingV2 } from '../../models/productv2/productpricingv2.model';
import { EnumArticleType } from '../../models/enum/enum-article-type';


@Injectable({
    providedIn: 'root'
})
export class ProductMapperV2Service {


    public groupProductsByService(products: ProductV2[]): { service: string, products: ProductV2[] }[] {
        const groups: { [key: string]: ProductV2[] } = {};

        products.forEach(product => {
            if (!groups[product.serviceLine]) {
                groups[product.serviceLine] = [];
            }
            groups[product.serviceLine].push(product);
        });

        return Object.entries(groups).map(([service, products]) => ({
            service,
            products
        }));
    }

    public hydrateProductV2(data: any): ProductV2 {

        let product: ProductV2;

        switch (data.productType) {
            case EnumProductType.KIT:
                product = new KitProductV2();
                break;

            case EnumProductType.PACK:
                product = new PackProductV2();
                break;

            case EnumProductType.ATOMIC:
            default:
                product = new AtomicProductV2();
                break;
        }

        Object.assign(product, data);

        // Backward compatibility: old cached docs may still expose `category`.
        product.family = product.family ?? data?.category;


        if (product.priceList) {
            product.priceList = this.hydratePriceList(product.priceList);
        }

        if (product instanceof KitProductV2 || product instanceof PackProductV2) {

            if (product.device) {
                Object.setPrototypeOf(product.device, AtomicProductV2.prototype);
                 product.device.priceList = this.hydratePriceList(product.device.priceList);
            }

            if (product.consumable) {
                Object.setPrototypeOf(product.consumable, AtomicProductV2.prototype);
                product.consumable.priceList = this.hydratePriceList(product.consumable.priceList);
            }
        }

        return product;
    }

    public hydratePriceList(priceList: any): PriceList {
        if (!priceList) {
            return priceList;
        }

        // Set prototype of priceList
        Object.setPrototypeOf(priceList, PriceList.prototype);

        if (priceList.productPricings) {
            priceList.productPricings = priceList.productPricings.map(pp => {
                Object.setPrototypeOf(pp, ProductPricingV2.prototype);

                pp.p90 = pp.p90 != null ? String(pp.p90) : null;
                pp.p50 = pp.p50 != null ? String(pp.p50) : null;
                pp.p10 = pp.p10 != null ? String(pp.p10) : null;
                pp.p50p90 = pp.p50p90 != null ? String(pp.p50p90) : null;
                pp.p10p50 = pp.p10p50 != null ? String(pp.p10p50) : null;

                return pp;
            });
        }

        return priceList;
    }



    // décortiquer les child de kit et pack en des products. ( case for duplication )
    createDistinctProductV2List(products: ProductWithChildrenV2[]): ProductV2[] {
        const distinctProducts: ProductV2[] = [];

        for (const product of products) {

            // --- 1) ATOMIC : le product lui-même ---
            if (product.productType === EnumProductType.ATOMIC) {
                distinctProducts.push(this.hydrateProductV2(product));
            }

            // --- 2) KIT ou PACK ---
            else if (
                product.productType === EnumProductType.KIT ||
                product.productType === EnumProductType.PACK
            ) {

                if (product.children?.length) {
                    for (const child of product.children) {

                        // Propagation du type depuis le parent
                        child.productType = product.productType;
                        child.type = product.type;
                        // Ajout du child comme product indépendant
                        distinctProducts.push(this.hydrateProductV2(child));
                    }
                }

                if (product.productType === EnumProductType.KIT) {
                    product.productType = EnumProductType.ATOMIC;
                    distinctProducts.push(this.hydrateProductV2(product));
                }
            }
        }

        return distinctProducts;
    }

    public buildDistinctProductsV2(products: ProductWithChildrenV2[]): ProductV2[] {

        if (!products) {
            return [];
        }

        // 1. Séparer parents et enfants
        const productChildList = products.filter(p => p['parentId'] != null);
        const parentIdsWithChildren = new Set(productChildList.map(pc => pc['parentId']));

        // 3. Supprimer les parents qui ont au moins une association dans leur children
        const filtered = products.filter(p => p['parentId'] != null || (p['parentId'] === null && !parentIdsWithChildren.has(p.id)));

        // 4. Les parents sans children → ATOMIC
        filtered.forEach(item => {
            if (item['parentId'] === null) {
                item.productType = EnumProductType.ATOMIC;
                item.children = null;
            }
        });

        return filtered.map(p => this.hydrateProductV2(p));
    }

    //to be deleted when migrate CMC to V2
    mapToProductWithChildrenV2(backend: any): ProductWithChildrenV2 {


        let product: ProductWithChildrenV2 = new ProductWithChildrenV2();
        this.initProduct(product, backend);
        let productPriceList = this.mapToPriceList(backend.classification);

        switch (backend.type) {

            case "APPAREIL":
                {
                    product.type = EnumArticleType.DEVICE;
                    if (backend.productConsumables && backend.productConsumables.length > 0) {
                        let _children = [];
                        product.productType = EnumProductType.KIT;
                        product.priceList = productPriceList;
                        backend.productConsumables.forEach(pc => {
                            let child = {};
                            child['id'] = pc.consumable.id;
                            child['allowance'] = backend.productDetails.franchise;
                            child['serviceLine'] = backend.serviceCode;
                            child['device'] = { ...product };
                            child['device'].productType = EnumProductType.ATOMIC;
                            child['device'].type = 'DEVICE';
                            child['consumable'] = this.mapToProductWithChildrenV2(pc.consumable);
                            _children.push(child);
                        });

                        product.children = _children;
                    } else {
                        product.productType = EnumProductType.ATOMIC;
                        product.priceList = productPriceList;
                    }
                    break;
                }
            case "CONSOMMABLE":
                product.type = EnumArticleType.CONSUMABLE;
                product.productType = EnumProductType.ATOMIC;
                product.priceList = productPriceList;
                break;
            case "PACK_DEVICE":
                product.type = EnumArticleType.DEVICE;
                product.productType = EnumProductType.PACK;
                product.priceList = null;
                if (backend.productConsumables && backend.productConsumables.length > 0) {
                    let _children = [];
                    backend.productConsumables.forEach(pc => {
                        let child = {};
                        child['id'] = pc.consumable.id;
                        child['priceList'] = this.mapToPriceList(pc.consumable.classification);
                        child['serviceLine'] = backend.serviceCode;
                        child['device'] = { ...product };
                        child['device'].productType = EnumProductType.ATOMIC;
                        child['device'].type = 'DEVICE';
                        child['consumable'] = this.mapToProductWithChildrenV2(pc.consumable);
                        child['consumable'].tyoe = 'CONSUMABLE'; //force PACK_CONSO
                        _children.push(child);
                    });

                    product.children = _children;
                }
                break;
        }

        return product;

    }

    private initProduct(product, backend) {

        product.id = backend.id;
        product.code = backend.codeElis;
        product.label = backend.label;
        product.type = backend.type;
        product.isCmc = true;
        product.serviceLine = backend.serviceCode;
        product.family = backend.productDetails?.family ?? backend.productDetails?.category;
        product.subfamily = backend.productDetails?.subFamily;
    }


    mapToPriceList(raw: any): PriceList {
        const classification = new PriceList();

        classification.id = raw.id;
        classification.groupingCode = raw.groupingCode;
        classification.salesRole = raw.role;
        classification.market = raw.market;
        classification.subMarket = raw.subMarket;
        classification.region = raw.region;

        classification.productPricings = (raw.productPricings || []).map(p => this.mapProductPricing(p));

        Object.setPrototypeOf(classification, PriceList.prototype);

        if (classification.productPricings) {
            classification.productPricings = classification.productPricings.map(pp => {
                Object.setPrototypeOf(pp, ProductPricingV2.prototype);
                pp.p90 = pp.p90 != null ? String(pp.p90) : null;
                pp.p50 = pp.p50 != null ? String(pp.p50) : null;
                pp.p10 = pp.p10 != null ? String(pp.p10) : null;
                pp.p50p90 = pp.p50p90 != null ? String(pp.p50p90) : null;
                pp.p10p50 = pp.p10p50 != null ? String(pp.p10p50) : null;
                return pp;
            });
        }

        return classification;
    }

    private mapProductPricing(raw: any): ProductPricingV2 {
        const priceList = new ProductPricingV2();

        priceList.id = raw.id;
        priceList.p90 = String(raw.p90);
        priceList.p50p90 = String(raw.p50p90);
        priceList.p50 = String(raw.p50);
        priceList.p10p50 = String(raw.p10p50);
        priceList.p10 = String(raw.p10);

        priceList['r7090'] = String(raw.r7090);
        priceList['r5090'] = String(raw.r5090);
        priceList['r3090'] = String(raw.r3090);
        priceList['r1090'] = String(raw.r1090);

        let productPricingDetails = raw.productPricingDetails;
        if (productPricingDetails) {
            priceList.invoicingMode = productPricingDetails.billingMode;
            priceList.frequency = productPricingDetails.frequency;
            priceList.franchiseConso = productPricingDetails.franchiseConso;
            priceList.extraPrice = productPricingDetails.extraPrice;
            priceList.numberOfChange = productPricingDetails.numberOfChange;
            priceList.exchangeRule = productPricingDetails.exchangeRule;

            priceList.stockVT = productPricingDetails.stockVT;
            priceList.contractType = productPricingDetails.contractType;
            priceList.unitOfMeasure = productPricingDetails.unitOfMeasure;
            priceList.forfaitCode = productPricingDetails.forfaitCode;
            priceList.forfaitLabel = productPricingDetails.forfaitLabel;
            priceList.minQuantity = productPricingDetails.quantityMin;
            priceList.maxQuantity = productPricingDetails.quantityMax;
        }
        return priceList;
    }


    public buildParentHierarchy(data: ProductWithChildrenV2[], isCmc: boolean, parentItem = null) {

        data.forEach(item => {
            if (parentItem !== null) {
                item['parentId'] = parentItem.id;
                item['uniqueKey'] = parentItem.id + '-' + item.id;
                item['productType'] = parentItem['productType'];
                item['type'] = parentItem['type'];
            } else {
                item['parentId'] = null;
                item['uniqueKey'] = item.id;
            }
            item.isCmc = isCmc;
            if (item.children && item.children.length > 0) {
                this.buildParentHierarchy(item.children as ProductWithChildrenV2[], isCmc, item);
            }
        });
        return data;
    }



}
